import { ReactElement, useState } from 'react';
import { Message, useMessage } from '@messageformat/react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { color, space } from '@op/opds.tokens';
import { Stepper } from '@op/opds.opux-stepper';
import { Tile, Row, List, ListItem, Form, FormRow } from '@op/opds.opux';
import { ReactComponent as Circle } from '../../../images/circle.svg';
import { ReactComponent as Line } from '../../../images/line.svg';
import { ReactComponent as Arrow1 } from '../../../images/arrow-vertical-full.svg';
import { ReactComponent as Arrow2 } from '../../../images/arrow-horizontal-full.svg';
import { dispersionGraphOptions } from '../utils';
import { data } from './data';
import { NoOpFormSubmitHandler } from '../../../util/formUtils';

interface IDataArray {
  y: number;
  marker: { enabled: boolean };
}

const INITIAL_POINTS_ENABLED = 2;

const DispersionGraph = (): ReactElement => {
  const [value, setValue] = useState(INITIAL_POINTS_ENABLED);
  const startDates = [
    6592000000, 375596800000, 4146441600000, 5592000000, 675596800000, 7146441600000,
    8592000000, 975596800000, 6146441600000, 4592000000,
  ];

  const pointInterval = [490, 32, 163, 555, 425, 621, 294, 359, 228, 98];
  const [averageArray, setAverageArray] = useState<IDataArray[]>([
    {
      y: 1222.226,
      marker: {
        enabled: true,
      },
    },
    {
      y: 123.678,
      marker: {
        enabled: true,
      },
    },
  ]);
  const [options, setOptions] = useState(
    dispersionGraphOptions(data, startDates[value - 1], averageArray),
  );

  const changeValue = (newValue: number): void => {
    let pointCount;
    if (Number.isNaN(newValue)) {
      pointCount = INITIAL_POINTS_ENABLED;
    }
    pointCount = Math.max(1, Math.min(newValue, 10));

    const enabledPoints = new Set(pointInterval.slice(0, pointCount));
    const updatedData = data.map((point, index) => ({
      y: point.y,
      marker: { enabled: enabledPoints.has(index) },
    }));
    const updatedAverageArray = updatedData.filter((point) => point.marker.enabled);
    setAverageArray(updatedAverageArray);
    setOptions(
      dispersionGraphOptions(
        updatedData,
        startDates[pointCount - 1],
        updatedAverageArray,
      ),
    );
    setValue(pointCount);
  };

  return (
    <div className="investoracademy-graph-wrapper">
      <Tile>
        <h4 className="investoracademy-general-graph-title">
          <Message id="course2.part2.dispersion_graph.title" />
        </h4>
        <p className="align-center" style={{ marginTop: space.small }}>
          <Message id="course2.part2.dispersion_graph.body" />
        </p>
        <div style={{ marginTop: space.small }}>
          <div style={{ display: 'none' }} id="dispersion_graph">
            <Message id="course2.part2.dispersion_graph.graph_accessibility_text" />
          </div>
          <div role="img" aria-labelledby="dispersion_graph">
            <div className="investoracademy-dispersiongraph-box">
              <div className="investoracademy-dispersiongraph-line-left">
                <Message id="course2.part2.dispersion_graph.price" />
                <Arrow1 style={{ flex: 1 }} aria-hidden="true" />
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { width: '100%' } }}
              />
            </div>

            <Row>
              <div className="investoracademy-dispersiongraph-line-bottom">
                <Arrow2 aria-hidden="true" />
                <Message id="course2.part2.dispersion_graph.time" />
              </div>
            </Row>
          </div>
          <Row>
            <div style={{ textAlign: 'center', margin: '0 auto' }}>
              <List
                border="none"
                size="tiny"
                indented
                style={{ display: 'inline-block', textAlign: 'left', margin: '0' }}
                aria-hidden="true"
              >
                <ListItem style={{ display: 'inline' }}>
                  <div className="investoracademy-listitem-container">
                    <Line
                      style={{ color: color.indicator.notification }}
                      aria-hidden="true"
                    />
                    <Message id="course2.part2.dispersion_graph.stock_rate" />
                  </div>
                </ListItem>
                <ListItem style={{ display: 'inline' }}>
                  <div className="investoracademy-listitem-container">
                    <Line
                      style={{ color: color.indicator.negative }}
                      aria-hidden="true"
                    />
                    <Message id="course2.part2.dispersion_graph.risk" />
                  </div>
                </ListItem>
                <ListItem style={{ display: 'inline' }}>
                  <div className="investoracademy-listitem-container">
                    <Circle fill={color.indicator.default} aria-hidden="true" />
                    <Message id="course2.part2.dispersion_graph.purchases" />
                  </div>
                </ListItem>
              </List>
            </div>
          </Row>
          <div className="investoracademy-vertical-flex-align">
            <div className="investoracademy-dispersiongraph-stepper-container">
              <Form stacked onSubmit={NoOpFormSubmitHandler}>
                <FormRow
                  label={useMessage('course2.part2.dispersion_graph.purchase_amount')}
                  required
                >
                  <Stepper min={1} max={10} initialValue={value} onChange={changeValue} />
                </FormRow>
              </Form>
            </div>
          </div>
        </div>
      </Tile>
    </div>
  );
};

export default DispersionGraph;
